import { FilterOption } from './Filters/Filters';
import { ENTITY_TYPES_OPTIONS, PLATFORM_OPTIONS, RISK_INDICATORS_OPTIONS, RISK_LEVELS_OPTIONS } from './Filters/FilterOptions';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ENTITY_STATUS_OPTIONS, getEntityStatusByInsightStatusDescriptions, getInsightStatusDescriptionsByEntityStatus } from '../common/entityStatus';

interface Params {
    riskLevelFilter?: number[];
    setRiskLevelFilter: Dispatch<SetStateAction<number[] | undefined>>;
    entityTypeFilter?: number[];
    setEntityTypeFilter: Dispatch<SetStateAction<number[] | undefined>>;
    riskIndicatorFilter?: string[];
    setRiskIndicatorFilter: Dispatch<SetStateAction<string[] | undefined>>;
    entityStatus?: string[];
    setEntityStatus: Dispatch<SetStateAction<string[] | undefined>>;
    idpFilter?: string[];
    setIdpFilter: Dispatch<SetStateAction<string[] | undefined>>;
    isWithStatusFilter?: boolean;
    hiddenRiskIndicators?: string[];
}

const useGetFilters = ({
    riskLevelFilter,
    setRiskLevelFilter,
    entityTypeFilter,
    setEntityTypeFilter,
    riskIndicatorFilter,
    setRiskIndicatorFilter,
    entityStatus,
    setEntityStatus,
    idpFilter,
    setIdpFilter,
    isWithStatusFilter,
    hiddenRiskIndicators = []
}: Params) => {
    const [filters, setFilters] = useState<FilterOption<string | number>[]>([]);

    useEffect(() => {
        const filters: FilterOption<string | number>[] = [
            {
                items: RISK_LEVELS_OPTIONS,
                onChange: setRiskLevelFilter as (value?: (string | number) | (string | number)[]) => void,
                selectedItems: riskLevelFilter,
                title: 'Risk level',
                filterIdentifier: 'riskLevel',
                variant: 'filter'
            },
            {
                items: RISK_INDICATORS_OPTIONS.filter(({ value }) => !hiddenRiskIndicators?.includes(value)),
                onChange: setRiskIndicatorFilter as (value?: (string | number) | (string | number)[]) => void,
                selectedItems: riskIndicatorFilter,
                title: 'Risk indicator',
                filterIdentifier: 'riskIndicator',
                menuClassName: '!w-[300px]',
                fullMenuWidth: true,
                variant: 'filter'
            }
        ];

        if (isWithStatusFilter) {
            filters.push({
                items: ENTITY_STATUS_OPTIONS,
                onChange: (value?: (string | number) | (string | number)[]) => {
                    const statuses = getInsightStatusDescriptionsByEntityStatus(value as string);

                    setEntityStatus(statuses);
                },
                selectedItems: getEntityStatusByInsightStatusDescriptions(entityStatus),
                title: 'Entity status',
                filterIdentifier: 'status',
                variant: 'filter'
            });
        }

        filters.push({
            items: ENTITY_TYPES_OPTIONS,
            onChange: setEntityTypeFilter as (value?: (string | number) | (string | number)[]) => void,
            selectedItems: entityTypeFilter,
            title: 'Entity type',
            filterIdentifier: 'entityType',
            variant: 'filter'
        });

        filters.push({
            items: PLATFORM_OPTIONS,
            onChange: setIdpFilter as (value?: (string | number) | (string | number)[]) => void,
            selectedItems: idpFilter,
            title: 'IdP',
            filterIdentifier: 'IdP',
            variant: 'filter'
        });

        setFilters(filters);
    }, [
        riskLevelFilter,
        entityTypeFilter,
        riskIndicatorFilter,
        entityStatus,
        idpFilter,
        setRiskLevelFilter,
        setEntityTypeFilter,
        setRiskIndicatorFilter,
        setEntityStatus,
        setIdpFilter
    ]);

    return filters;
};

export default useGetFilters;
